import React, { useState } from 'react';
import { composeQueryString } from '../../../lib/Query';
import { ContactSchema, RoomSchema } from '../../../definitions/schemas';
import { Events, Properties } from '../../../enums/Analytics';
import {
  markWelcomeMessageAsViewed,
  removeObsoleteWelcomeMessageViewed,
  shouldDisplayWelcomeMessageOverlay,
} from '../components/WelcomeMessage/helper';
import { Query } from '../../../definitions/query';
import Analytics from '../../../tools/Analytics';
import ContactIdentificationDialog from './ContactIdentificationDialog';
import ContactsInterface from '../../../graphql/Contacts/ContactsInterface';
import RoomSecurity from './Security';
import UserStore from '../../../stores/observables/UserStore';
import { UnifiedConsentType } from '../../../enums/UnifiedConsentType';

interface IContext {
  isAnonymousContact: boolean;
  isGuestVisitor: boolean;
  queryStringParams?: Query;
  verifyContactIdentity: (
    referrer: string,
    customQueryStringParams?: Query
  ) => void;
}

const RoomViewContext: React.Context<IContext> = React.createContext<IContext>({
  isAnonymousContact: false,
  /**
   * Defaulting to true for scenarios where a room is viewed outside the
   * public view page. This avoids triggering authenticated user actions
   * that are meant to be performed by guest visitors only.
   *
   * For example: room preview dialog. In this case, we avoid tracking
   * user actions like viewing contents, since we contextualize the scenario
   * as being viewed by a guest visitor with this default value.
   */
  isGuestVisitor: true,
  queryStringParams: undefined,
  verifyContactIdentity: () => {},
});

type RoomViewContextProviderProps = React.PropsWithChildren<{
  contact: ContactSchema | undefined;
  hasDeal: boolean;
  /**
   * Flag telling the children that the visitor is a guest and must
   * be treated as such, allowing requests performed deep in the
   * hierarchy to properly handle this scenario, for tracking purposes.
   *
   * @note Even authenticated users could be treated as guests visitors
   * if they're trying to access a room that doesn't belong to one of
   * the orgs they belong to OR if they're accessing one of their org's
   * rooms for internal audience purposes.
   */
  isGuestVisitor: boolean;
  /**
   * Query string parameters that were present on the initial room loading,
   * which will be put available globally, to avoid prop-drilling these params
   */
  queryStringParams?: Query;
  /**
   * An (optional) object containing any key-value pair of query string
   * params to maintain after the page is refreshed when an email has
   * been provided.
   */
  refreshQueryStringParams?: Query;
  roomContactsCount?: number;
  roomId: string;
  uuid: string;
  welcomeMessage: RoomSchema['welcomeMessage'];
  isConsentDeclinedSuppression?: boolean;
  shouldUseUnifiedConsent?: boolean;
}>;

const RoomViewContextProvider: React.FC<RoomViewContextProviderProps> = (
  props: RoomViewContextProviderProps
): JSX.Element => {
  const {
    contact,
    hasDeal,
    isGuestVisitor,
    queryStringParams,
    refreshQueryStringParams: initialRefreshQueryStringParams,
    roomContactsCount,
    roomId,
    uuid,
    welcomeMessage,
    isConsentDeclinedSuppression,
    shouldUseUnifiedConsent,
  } = props;

  const [identifyContact, setIdentifyContact] = useState(false);
  const [saving, setSaving] = useState(false);
  const [referrer, setReferrer] = useState('');
  const [refreshQueryStringParams, setRefreshQueryStringParams] = useState(
    initialRefreshQueryStringParams
  );

  const onCancelContactIdentification = () => {
    setIdentifyContact(false);
  };

  const verifyContactIdentity = (
    referrer: string,
    customQueryStringParams?: Query
  ) => {
    setIdentifyContact(true);
    setReferrer(referrer);

    if (customQueryStringParams) {
      setRefreshQueryStringParams((prev) => ({
        ...(prev ?? {}),
        ...customQueryStringParams,
      }));
    }

    Analytics.track(Events.DEAL_ROOM_EMAIL_REQUESTED, {
      [Properties.CONTACT_NUMBER]: roomContactsCount,
      [Properties.ENTERED_FROM]: referrer,
      [Properties.HAS_DEAL]: hasDeal,
    });

    if (shouldUseUnifiedConsent && isConsentDeclinedSuppression) {
      Analytics.track(Events.DSR_CONSENT_VIEWED, {
        type: UnifiedConsentType.CONSENT_FROM_CHAT,
      });
    }
  };

  /**
   * If the current anonymous visitor has seen the welcome message and
   * then authenticates by entering an email (which could change the id
   * of the contact visitor), we mark the welcome message as viewed
   * for that new contact
   */
  const updateWelcomeMessageViewStatus = (newContact: ContactSchema) => {
    if (!(isGuestVisitor && welcomeMessage?.id)) {
      return;
    }

    const currentUser = UserStore.get();
    const visitorId = contact?.id || currentUser?.id;

    const welcomeMessageSeen =
      visitorId &&
      !shouldDisplayWelcomeMessageOverlay(visitorId, welcomeMessage);

    if (welcomeMessageSeen) {
      removeObsoleteWelcomeMessageViewed(visitorId, welcomeMessage);
      markWelcomeMessageAsViewed(newContact.id, welcomeMessage);
    }
  };

  const handleContactIdentification = async (email: string) => {
    try {
      if (!contact) {
        return;
      }

      setSaving(true);

      // updating the anonymous contact, setting the new email which will cause
      // the contact to not be treated as anonymous anymore
      const res = await new ContactsInterface(contact.id).update({
        email,
        roomId,
      });

      const updatedContact = res.updateContact.contact;
      const persistedAccessData = RoomSecurity.getAccess(uuid);

      RoomSecurity.flushAccess(uuid);

      // updating the room security access data cookie, to keep the contact
      // information up to date and making sure the contact is not treated
      // as anonymous anymore
      RoomSecurity.persistAccess(uuid, {
        ...(persistedAccessData || {}),
        contact: updatedContact,
      });

      // if the anonymous contact has seen the welcome message and then
      // entered an email, if that email belongs to an existing contact with a
      // different id, then we mark it as viewed for that contact as well
      updateWelcomeMessageViewStatus(updatedContact);

      Analytics.track(Events.DEAL_ROOM_EMAIL_PROVIDED, {
        [Properties.CONTACT_NUMBER]: roomContactsCount,
        [Properties.ENTERED_FROM]: referrer,
        [Properties.HAS_DEAL]: hasDeal,
        [Properties.IS_ANONYMOUS_EXISTING_CONTACT]:
          updatedContact.id !== contact.id,
      });

      const query = refreshQueryStringParams
        ? composeQueryString(refreshQueryStringParams)
        : '';

      if (query.length) {
        window.location.href = `${window.location.pathname}${query}`;
      } else {
        window.location.reload();
      }
    } catch (error) {
      setSaving(false);
    }
  };

  const context = {
    isAnonymousContact: !!contact?.anonymous,
    isGuestVisitor,
    queryStringParams,
    verifyContactIdentity,
  };

  return (
    <RoomViewContext.Provider value={context}>
      {props.children}
      <ContactIdentificationDialog
        shouldUseUnifiedConsent={shouldUseUnifiedConsent}
        isConsentDeclinedSuppression={isConsentDeclinedSuppression}
        onCancel={onCancelContactIdentification}
        onSubmit={handleContactIdentification}
        open={identifyContact}
        saving={saving}
      />
    </RoomViewContext.Provider>
  );
};

export default RoomViewContextProvider;
export { RoomViewContext };
